import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

const buttonVariants = cva(
  [
    'inline-flex items-center flex-row gap-2 justify-center rounded-[8px] text-sm font-normal transition-colors focus-visible:outline-none  disabled:pointer-events-none disabled:opacity-50',
    'ea-button',
  ],
  {
    variants: {
      variant: {
        default: ['bg-primary text-primary-foreground hover:bg-primary/90', 'ea-button--default'],
        destructive: [
          'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
          'ea-button--destructive',
        ],
        accent: [
          'bg-accent text-accent-foreground shadow-sm hover:bg-accent/90',
          'ea-button--accent',
        ],
        outline: [
          'border border-input bg-transparent shadow-sm hover:bg-secondary hover:text-secondary-foreground',
          'ea-button--outline',
        ],
        secondary: [
          'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
          'ea-button--secondary',
        ],
        white: ['bg-white text-foreground hover:bg-white/80', 'ea-button--white'],
        ghost: ['text-secondary-foreground hover:bg-secondary', 'ea-button--ghost'],
        link: ['text-primary underline-offset-4 hover:underline', 'ea-button--link'],
        dark: ['bg-foreground text-background shadow hover:bg-foreground/90', 'ea-button--dark'],
        rating: ['text-rating bg-rating/20 hover:bg-rating/25'],
        none: ['bg-transparent shadow-none hover:opacity-80', 'ea-button--none'],
        'default-alt': ['text-primary bg-primary/20  hover:bg-primary/25'],
        'destructive-alt': ['text-destructive bg-destructive/20 hover:bg-destructive/25'],
        'accent-alt': ['text-accent bg-accent/20 hover:bg-accent/25'],
      },
      size: {
        default: ['h-12 px-6 py-2', 'ea-button--size-default'],
        none: ['p-0 w-fit h-fit', 'ea-button--size-none'],
        xs: ['h-8 px-3 text-xs', 'ea-button--size-xs'],
        sm: ['h-10 px-6 text-sm', 'ea-button--size-sm'],
        lg: ['h-14 px-8', 'ea-button--size-lg'],
        icon: ['h-10 w-10', 'ea-button--size-icon'],
        'icon-sm': ['h-7 w-7', 'ea-button--size-icon-sm'],
        'icon-xs': ['h-5 w-5', 'ea-button--size-icon-xs'],
        'icon-lg': ['h-12 w-12', 'ea-button--size-icon-lg'],
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, isLoading, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    const blocked = Boolean(props.disabled || isLoading);

    // Keep the original DOM/content in flow: no wrapper may change flex gaps,
    // responsive labels, intrinsic width, or the accessible name during loading.
    const loadingContent = (label: React.ReactNode) => (
      <>
        {label}

        {isLoading && (
          <span
            data-ea-button-spinner=""
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 flex items-center justify-center"
            style={{ margin: 0 }}
          >
            <Spinner />
          </span>
        )}
      </>
    );
    const content =
      asChild && React.isValidElement<React.HTMLAttributes<HTMLElement>>(children)
        ? React.cloneElement(children, {
            children: loadingContent(children.props.children),
            ...(isLoading && {
              style: { ...children.props.style, WebkitTextFillColor: 'transparent' },
            }),
            // Native disabled does not disable links. Inert blocks pointer and
            // keyboard activation without adding client-only event handlers.
            ...(blocked && {
              tabIndex: -1,
              inert: true,
            }),
          })
        : asChild
          ? children
          : loadingContent(children);
    return (
      <Comp
        className={cn(
          isLoading && 'relative [&>*:not([data-ea-button-spinner])]:opacity-0',
          cn(buttonVariants({ variant, size, className })),
        )}
        ref={ref}
        {...props}
        style={isLoading ? { ...props.style, WebkitTextFillColor: 'transparent' } : props.style}
        disabled={blocked}
        aria-disabled={blocked}
        aria-busy={isLoading || props['aria-busy']}
        data-ea-button-loading={isLoading || undefined}
      >
        {content}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
